import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';
import { PlatformLoader } from '../platform/platform.loader';
import { TemplateAPI } from '../template/template.api';
import { PredictiveBundleAPI } from './predictive-bundle.api';
import { PredictiveBundleConstants } from './predictive-bundle.constants';
import type {
  BuildCartPayloadOptions,
  BundleableProduct,
  BundleableSelectedOption,
  BundleAction,
  BundleCartItem,
  BundleDiscount,
  BundlePriceResult,
  BundleProduct,
  BundleSessionData,
  BundleTemplateName,
  BundleUpdateData,
  DiscountedPriceResult,
  FormatPriceParams,
  PredictiveBundleData,
  PredictiveBundleWidgetDesignSettings,
} from './predictive-bundle.model';

/**
 * Provides predictive bundle business logic, pricing calculations, and template access.
 *
 * Most shop-specific customization should happen by extending this service instead of
 * editing controller flows directly.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('PredictiveBundleService', (PredictiveBundleService) => {
 *   return class extends PredictiveBundleService {
 *     getWidgetSettings(widgetId) {
 *       const settings = super.getWidgetSettings(widgetId);
 *       return {
 *         ...settings,
 *         widgetDesignSettings: {
 *           ...settings.widgetDesignSettings,
 *           buttonText: 'Build my bundle',
 *         },
 *       };
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class PredictiveBundleService {
  constructor(
    protected appService: AppService,
    protected platformLoader: PlatformLoader,
    protected templateAPI: TemplateAPI,
    protected predictiveBundleAPI: PredictiveBundleAPI,
    protected constants: PredictiveBundleConstants
  ) {}

  get widgets() {
    return (
      this.appService.TAEAppConfig.recommendationWidgets?.[this.constants.PREDICTIVE_BUNDLE_TYPE] ||
      {}
    );
  }

  get defaultSettings() {
    return (
      this.appService.TAEAppConfig.recommendationWidgets?.dynamicBundleDefaultSettings ||
      this.constants.DEFAULT_SETTINGS
    );
  }

  isValidBundle(widgetId?: string): boolean {
    if (!widgetId || widgetId === '') return false;

    const recommendationWidgets = this.appService.TAEAppConfig.recommendationWidgets;
    if (!recommendationWidgets || Object.keys(recommendationWidgets).length === 0) return false;

    const predictiveBundleWidgets = recommendationWidgets[this.constants.PREDICTIVE_BUNDLE_TYPE];
    if (!predictiveBundleWidgets || Object.keys(predictiveBundleWidgets).length === 0) return false;

    return true;
  }

  /**
   * Loads the Liquid template used to render a bundle surface.
   *
   * @param templateName - Template identifier to resolve from TAE template metadata.
   * @returns Template source as a string.
   */
  async getWidgetTemplate(
    templateName: BundleTemplateName = this.constants.BUNDLE_TEMPLATE_NAME.dynamic
  ): Promise<string> {
    const { templateMetadata } = this.appService.TAEAppConfig;
    const templateId = templateMetadata?.[templateName];

    const { templates } = await this.templateAPI.getWidgetTemplate({
      name: templateName,
      id: templateId,
    });

    return templates[templateId] || '';
  }

  /**
   * Merges widget-specific design settings with predictive bundle defaults.
   *
   * @param widgetId - Widget id to resolve settings for.
   * @returns Normalized widget design settings.
   */
  getWidgetSettings(widgetId: string): {
    widgetDesignSettings: PredictiveBundleWidgetDesignSettings;
  } {
    let widgetDesignSettings =
      this.appService.TAEAppConfig.recommendationWidgets?.[this.constants.PREDICTIVE_BUNDLE_TYPE]?.[
        widgetId
      ]?.widgetDesignSettings || {};

    widgetDesignSettings = {
      ...this.defaultSettings,
      ...widgetDesignSettings,
    };

    const { titleFontStyle } = widgetDesignSettings;
    if (titleFontStyle) {
      const { fontWeight, fontStyle } = this.appService.splitGoogleFontStyle(titleFontStyle);
      widgetDesignSettings.titleFontWeight = fontWeight;
      widgetDesignSettings.titleFontStyle = fontStyle;
    }

    return { widgetDesignSettings };
  }

  /**
   * Formats a monetary value using the current shop currency settings.
   *
   * @param params - Formatting configuration.
   * @returns Localized currency string.
   */
  formatPrice(params: FormatPriceParams): string {
    const { value, showCurrencyCodes, showCentAsSuperscript, removeDecimalPoint } = params;

    const formatString = this.appService.TAEAppConfig.shop?.money_format || '${{amount}}';

    return this.appService.formatCurrency(value, {
      format: formatString,
      showCentAsSuperscript,
      removePriceDecimal: removeDecimalPoint,
      showCurrencyCodes,
    });
  }

  private resolveProductImage(
    product: BundleProduct,
    selectedVariant?: ProductItemVariant
  ): string | null {
    if (selectedVariant?.image) {
      return selectedVariant.image;
    }

    if (product.original_images?.[0]?.src) {
      return product.original_images[0].src;
    }

    if (product.images_info?.[0]?.src) {
      return product.images_info[0].src;
    }

    return (
      Object.values(product.images || {}).find((image): image is string => Boolean(image)) || null
    );
  }

  /**
   * Calculates derived bundle state such as selected variants, totals, and discount values.
   *
   * Override this method to inject shop-specific bundle business rules while keeping the
   * controller rendering flow unchanged.
   *
   * @param bundle - Raw bundle payload.
   * @param updateData - Variant or product selection change data.
   * @param action - Action that triggered recalculation.
   * @returns Normalized bundle state ready for rendering.
   */
  calculateBundleData(
    bundle: PredictiveBundleData,
    updateData: BundleUpdateData = {},
    action: BundleAction = this.constants.BUNDLE_ACTION.initData
  ): PredictiveBundleData {
    let { products } = bundle;

    if (bundle.bundle_type?.startsWith('ai_')) {
      products = [...bundle.products];
    }

    let totalPrice = 0;

    for (const product of products) {
      product.id = Number(product.id);

      if (product.available) {
        if (!action || action === this.constants.BUNDLE_ACTION.initData) {
          product.checked = true;
        } else if (
          action === this.constants.BUNDLE_ACTION.clickCheckbox &&
          String(product.id) === String(updateData.productId)
        ) {
          product.checked = !product.checked;
        }
      } else {
        product.checked = false;
      }

      if (product.variants.length > 0) {
        product.variants = product.variants.filter((v) => v.available);

        let selectedVariant;

        if (product.selectedOption?.id) {
          selectedVariant = product.variants.find(
            (variant) => String(variant.id) === String(product.selectedOption.id)
          );
        }

        if (!selectedVariant) {
          selectedVariant = product.variants.reduce((minVariant, currentVariant) =>
            parseFloat(currentVariant.price) < parseFloat(minVariant.price)
              ? currentVariant
              : minVariant
          );
        }

        if (
          action === this.constants.BUNDLE_ACTION.changeVariant &&
          updateData.productId &&
          updateData.variantId &&
          String(updateData.productId) === String(product.id)
        ) {
          selectedVariant = product.variants.find(
            (variant) => String(variant.id) === String(updateData.variantId)
          );
        }

        if (selectedVariant) {
          const price = Number(selectedVariant.price || 0);
          const image = this.resolveProductImage(product, selectedVariant);

          product.selectedOption = {
            rawPrice: price,
            title: selectedVariant.title,
            price: this.formatPrice({ value: price }),
            originPrice: price,
            minPrice: this.formatPrice({ value: Number(product.price_min || 0) }),
            id: selectedVariant.id,
            image,
          };

          product.image = image;
        }
      }

      if (product.checked && product.selectedOption) {
        totalPrice += product.selectedOption.rawPrice;
      }
    }

    const isSaleChecked = products.every((p) => p.checked) && (bundle.discount_value || 0) > 0;
    let totalBundlePrice = totalPrice;

    if (isSaleChecked) {
      const currencyRate = Number(window.Shopify?.currency?.rate || 1);
      if (bundle.discount_type === 'percent') {
        totalBundlePrice = totalPrice - (totalPrice * bundle.discount_value) / 100;
      } else {
        const discountAmount = bundle.discount_value * currencyRate;
        totalBundlePrice = totalPrice >= discountAmount ? totalPrice - discountAmount : 0;
      }
    }

    const hasSelectedProduct = products.filter((p) => p.available).length > 0;

    if (bundle.currentProductId) {
      const index = products.findIndex((p) => p.id === bundle.currentProductId);
      if (index !== -1) {
        const [product] = products.splice(index, 1);
        products.unshift(product);
      }
    }

    const currencyRate = Number(window.Shopify?.currency?.rate || 1);
    const amountValueDisplay =
      bundle.discount_type === 'percent'
        ? `${bundle.discount_value}%`
        : bundle.discount_value === 0
          ? 0
          : this.formatPrice({ value: Math.abs(bundle.discount_value * currencyRate) });

    return {
      ...bundle,
      products,
      rawTotalPrice: totalPrice,
      rawTotalBundlePrice: totalBundlePrice,
      totalPrice: this.formatPrice({ value: totalPrice }),
      totalBundlePrice: this.formatPrice({ value: totalBundlePrice }),
      amountValueDisplay,
      hasSelectedProduct,
      bundleStyle: 'style2',
    };
  }

  saveBundleSessionData(bundle: PredictiveBundleData): void {
    const oldData = this.getBundleSessionData();
    const newData: Record<string, BundleSessionData> = {
      ...oldData,
      [bundle.widgetId]: {
        widgetId: bundle.widgetId,
        bundle_type: bundle.bundle_type,
        recommendationType: bundle.recommendation_type,
        primary_algorithm: bundle.primary_algorithm || '',
        secondary_algorithm: bundle.secondary_algorithm || '',
        amountValueDisplay: bundle.amountValueDisplay || '',
        totalBundlePrice: bundle.totalBundlePrice || 0,
        bundleProductIds: bundle.products.map((p) => p.id),
      },
    };

    try {
      sessionStorage.setItem(this.constants.SESSION_KEYS.DATA_SESSION, JSON.stringify(newData));
    } catch {}
  }

  getBundleSessionData(): Record<string, BundleSessionData> {
    try {
      const data = sessionStorage.getItem(this.constants.SESSION_KEYS.DATA_SESSION);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  /**
   * Proxies predictive bundle loading to the API layer.
   *
   * @param productId - Product id used to load bundle recommendations.
   * @returns Predictive bundles for the product, or an empty array when unavailable.
   */
  async getPredictiveBundles(productId: number): Promise<PredictiveBundleData[]> {
    return this.predictiveBundleAPI.getPredictiveBundles(productId);
  }

  getRawPrice(selectedOption: { originPrice?: number | string; rawPrice?: number }): number {
    if (selectedOption.rawPrice !== undefined) {
      return selectedOption.rawPrice;
    }
    if (typeof selectedOption.originPrice === 'number') {
      return selectedOption.originPrice;
    }
    if (typeof selectedOption.originPrice === 'string') {
      const numericValue = Number(selectedOption.originPrice);
      return Number.isNaN(numericValue) ? 0 : numericValue;
    }
    return 0;
  }

  calculateTotalPriceWithDetails<T extends BundleableProduct>(products: T[]): BundlePriceResult {
    let rawTotal = 0;
    let checkedCount = 0;

    for (const product of products) {
      if (product.checked && product.selectedOption) {
        rawTotal += this.getRawPrice(product.selectedOption);
        checkedCount++;
      }
    }

    return { rawTotal, checkedCount };
  }

  calculateDiscountedPrice<T extends BundleableProduct>(
    products: T[],
    discount: BundleDiscount,
    currencyRate = 1
  ): DiscountedPriceResult {
    const { rawTotal, checkedCount } = this.calculateTotalPriceWithDetails(products);
    const allChecked = products.length > 0 && products.every((p) => p.checked);
    const discountApplied = allChecked && discount.value > 0;

    let discountedTotal = rawTotal;

    if (discountApplied) {
      if (discount.type === 'percent') {
        discountedTotal = rawTotal - (rawTotal * discount.value) / 100;
      } else {
        const discountAmount = discount.value * currencyRate;
        discountedTotal = rawTotal >= discountAmount ? rawTotal - discountAmount : 0;
      }
    }

    return {
      rawTotal,
      checkedCount,
      discountedTotal,
      discountApplied,
    };
  }

  buildCartPayload<T extends BundleableProduct & { selectedOption: BundleableSelectedOption }>(
    products: T[],
    options: BuildCartPayloadOptions = {}
  ): BundleCartItem[] {
    const { properties = {}, quantity = 1 } = options;

    return products
      .filter((p) => p.checked && p.selectedOption?.id)
      .map((p) => ({
        id: String(p.selectedOption.id),
        quantity,
        selling_plan: null,
        properties,
      }));
  }

  filterAvailableProducts<T extends { available: boolean }>(products: T[]): T[] {
    return products.filter((p) => p.available);
  }

  filterCheckedProducts<T extends BundleableProduct>(products: T[]): T[] {
    return products.filter((p) => p.checked);
  }

  hasSelectedProducts<T extends BundleableProduct>(products: T[]): boolean {
    return products.some((p) => p.checked);
  }

  calculateTotalPrice(products: BundleProduct[]): number {
    return this.calculateTotalPriceWithDetails(products).rawTotal;
  }

  getLocale(): string {
    return (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
  }
}
